import { Chart } from '@antv/g2';
import { FC, useEffect, useRef, useMemo } from 'react';
import StackedBarChartOptions from './StackedBarChartOptions';
import { ChartCommonProps, ChartConfig, applyConfig } from '../../../utils/BaseChart/BaseChartOptions';
import { formatNumWithKMB, formatXAxisLabel, CHART_FONT_FAMILY_CSS } from '../../../utils/formatters';

export interface LegendItem {
    label: string;
    color: string;
}

const DEFAULT_VIEW_PROPS = {
    legend: {
        position: 'top' as const,
    },
    axis: {
        x: {
            labelAutoWrap: true,
            labelAutoHide: true,
            labelFontSize: 13,
            labelFontFamily: CHART_FONT_FAMILY_CSS,
            labelStroke: '#000',
            labelOpacity: 0.4,
            labelFontWeight: 400,
            labelFormatter: (text: string) => formatXAxisLabel(String(text ?? '')),
        },
        y: {
            position: 'left' as const,
            grid: true,
            gridStrokeOpacity: 0.2,
            labelFormatter: (datum: unknown) => formatNumWithKMB(Number(datum)),
            labelAutoRotate: false,
            labelFontSize: 13,
            labelFontFamily: CHART_FONT_FAMILY_CSS,
            labelStroke: '#000',
            labelOpacity: 0.4,
            labelFontWeight: 400,
        },
    },
    scale: {
        x: { padding: 0.5 },
    },
};

const DEFAULT_SIZE_ATTRIBUTES = {
    paddingTop: 20,
    paddingBottom: 60,
    paddingLeft: 20,
    paddingRight: 20,
};

type StackedBarChartProps = ChartCommonProps & {
    barsConfig: ChartConfig[];
    barClickHandler?: (...args: any[]) => any;
    legend?: LegendItem[];
};

const StackedBarChart: FC<StackedBarChartProps> = (props) => {
    const {
        className,
        data,
        barsConfig,
        sizeAttributes,
        viewLevelAdditionalProps,
        additionalMarks,
        barClickHandler,
        legend,
    } = props;
    const chartContainer = useRef<HTMLDivElement | null>(null);
    const chartInstance = useRef<Chart | null>(null);

    const chartOptions = useMemo(() => {
        const barIDs = barsConfig.map((_, index) => `bar${index + 1}`);
        const options = new StackedBarChartOptions(data, barIDs);

        const mergedSizeAttributes = {
            ...DEFAULT_SIZE_ATTRIBUTES,
            ...sizeAttributes,
        };
        options.setAttributes(mergedSizeAttributes);

        if (additionalMarks?.length) options.setChildren(additionalMarks);

        barsConfig.forEach((barConfig, index) => {
            const barKey = `bar${index + 1}`;
            applyConfig(options, barConfig, barKey);
        });

        const customLegend = legend && legend.length > 0;
        const mergedViewProps = {
            ...DEFAULT_VIEW_PROPS,
            ...viewLevelAdditionalProps,
            ...(customLegend ? { legend: false } : {}),
            axis: {
                ...viewLevelAdditionalProps?.axis,
                x: { ...DEFAULT_VIEW_PROPS.axis.x, ...viewLevelAdditionalProps?.axis?.x },
                y: { ...DEFAULT_VIEW_PROPS.axis.y, ...viewLevelAdditionalProps?.axis?.y },
            },
            scale: {
                ...viewLevelAdditionalProps?.scale,
                x: { ...DEFAULT_VIEW_PROPS.scale?.x, ...viewLevelAdditionalProps?.scale?.x },
            },
        };
        options.setViewAdditionalProps(mergedViewProps);

        return options.getOptions();
    }, [data, barsConfig, sizeAttributes, viewLevelAdditionalProps, additionalMarks, legend]);

    useEffect(() => {
        if (chartContainer.current) {
            chartInstance.current = new Chart({ container: chartContainer.current });
            barClickHandler && chartInstance.current.on('interval:click', barClickHandler);
            chartInstance.current.options(chartOptions);
            chartInstance.current
                .render()
                .then(() => {
                    // Force layout recalculation after initial render to fix X-axis label wrap/ellipsis
                    chartInstance.current?.forceFit?.();
                })
                .catch((error) => console.error('Render failed', error));
        }

        return () => {
            chartInstance.current?.destroy();
            chartInstance.current && barClickHandler && chartInstance.current.off('interval:click', barClickHandler);
        };
    }, [chartOptions, barClickHandler]);

    useEffect(() => {
        const container = chartContainer.current;
        const chart = chartInstance.current;
        if (!container || !chart?.forceFit) return;

        const resizeObserver = new ResizeObserver(() => {
            chart.forceFit?.();
        });
        resizeObserver.observe(container);
        return () => resizeObserver.disconnect();
    }, [chartOptions]);

    const hasCustomLegend = legend && legend.length > 0;

    if (hasCustomLegend) {
        return (
            <div
                className={`w-full min-w-0 flex flex-col ${className}`}
                data-test={props['data-test']}
                data-asper-stacked-barchart
            >
                <div className="flex justify-center py-3">
                    <div className="flex items-center gap-4 flex-wrap justify-center">
                        {legend!.map((item) => (
                            <div key={`legend-${item.label}`} className="flex items-center gap-2">
                                <div
                                    className="w-3 h-3 rounded-sm shrink-0"
                                    style={{ backgroundColor: item.color }}
                                />
                                <span className="font-['Open_Sans'] font-normal text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)]">
                                    {item.label}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
                <div ref={chartContainer} className="w-full min-w-0 flex-1" />
            </div>
        );
    }

    return (
        <div
            ref={chartContainer}
            className={`w-full min-w-0 ${className}`}
            data-test={props['data-test']}
            data-asper-stacked-barchart
        />
    );
};

export default StackedBarChart;
